/* eslint-disable no-nested-ternary */
import React from "react";
import { classnames, Icon, Panel, PlainButton, Tooltip } from "@culturehq/components";

import { useCompanies, useDepartments, useInterests, useLocations, useProfile, useQueryString, useSkills } from "app/context";
import { useDoor, useFieldsLabels } from "app/hooks";
import { canCreateEvent, canViewCompaniesField, canViewSkillsField } from "app/permissions";
import { getFiltersOptions, t, Trans } from "app/utils";

import CompanyFilter, { CompanyFilterState } from "components/filters/CompanyFilter";
import DepartmentFilter, { DepartmentFilterState } from "components/filters/DepartmentFilter";
import InterestFilter, { InterestFilterState } from "components/filters/InterestFilter";
import LocationFilter, { LocationFilterState } from "components/filters/LocationFilter";
import ClassYearFilter, { ClassYearFilterState } from "components/filters/ClassYearFilter";
import ClubFilter, { ClubFilterState } from "components/filters/ClubFilter";
import ProgramFilter, { ProgramFilterState } from "components/filters/ProgramFilter";
import DegreeFilter, { DegreeFilterState } from "components/filters/DegreeFilter";
import FilterGroup from "components/common/FilterGroup";

import CommunityFilter, { CommunityFilterState } from "./filters/CommunityFilter";
import SkillFilter, { SkillFilterState } from "./filters/SkillFilter";

import styles from "./styles.module.scss";

const CommunityFilters = ({
  onCreateGroupEvent, options, pagination: { totalCount }, query, layout, loading, onLayoutChange
}) => {
  const { profile } = useProfile();
  const labels = useFieldsLabels();
  const { organization } = profile;
  const { isEducational } = organization;
  const { onClearQueryString } = useQueryString();
  const { open: openOnMobile, onToggle: onToggleOpenOnMobile } = useDoor(false);
  const isAFilterApplied = Object.entries(options).map(option => option[1])
    .filter(array => array).length > 0;

  const { skills } = useSkills();
  const { companies } = useCompanies();
  const { departments } = useDepartments();
  const { interests } = useInterests();
  const { locations } = useLocations();

  const filters = {
    locations: getFiltersOptions("locationIds", locations, options),
    companies: getFiltersOptions("companyIds", companies, options),
    departments: getFiltersOptions("departmentIds", departments, options),
    interests: getFiltersOptions("interestIds", interests, options),
    skills: getFiltersOptions("skillIds", skills, options)
  };

  const peopleWord = totalCount === 1 ? "person" : "people";
  const resultsText = totalCount ? `${t("Showing")} ${totalCount} ${peopleWord}` : t(`No ${peopleWord}`);

  const getCommunityFilter = () => {
    switch (options.community) {
      case "connections":
        return ["you are following"];
      case "connected":
        return ["are your followers"];
      case "suggestions":
        return ["you are suggested to follow"];
      default:
        return [];
    }
  };

  return (
    <>
      <div className="row">
        {canCreateEvent(profile) && organization.eventsEnabled && (
          <div className="Filters__button col-xs-6 col-lg-12">
            <button
              type="button"
              className="button create"
              onClick={onCreateGroupEvent}
              disabled={query === null && Object.keys(options).every(name => !options[name])}
            >
              <Tooltip tip={t("Create an event for the group of users that are represented by the active filters.")}>
                <Icon icon="calendar-add" />
                <span><Trans>Create Group Event</Trans></span>
              </Tooltip>
            </button>
          </div>
        )}
        <div
          className={
            classnames(
              "Filters__button",
              "toggle-filters",
              "col-lg-12",
              canCreateEvent(profile) && organization.eventsEnabled ? "col-xs-6" : "col-xs-12"
            )
          }
        >
          <PlainButton onClick={onToggleOpenOnMobile} className="button toggle-filters">
            <Icon icon="person-check" />
            {openOnMobile ? <Trans>Hide Filters</Trans> : <Trans>Find People</Trans>}
          </PlainButton>
        </div>
      </div>
      <Panel className={openOnMobile ? "open" : "closed"}>
        <div className="chq-pan--hd">
          <div className={styles.views}>
            <PlainButton
              aria-label={t("List view")}
              onClick={() => onLayoutChange("list")}
              className={
                classnames(styles["views--view"], {
                  [styles["views--view-active"]]: layout === "list"
                })
              }
            >
              <Icon icon="view-list" />
            </PlainButton>
            {" "}
            <PlainButton
              aria-label={t("Grid view")}
              onClick={() => onLayoutChange("grid")}
              className={
                classnames(styles["views--view"], {
                  [styles["views--view-active"]]: layout === "grid"
                })
              }
            >
              <Icon icon="view-grid" />
            </PlainButton>
          </div>
          <h1><Trans>Find People</Trans></h1>
          <hr />
        </div>
        <Panel.Body>
          <CommunityFilter />
          <LocationFilter />
          {!isEducational && (<DepartmentFilter />)}
          {canViewCompaniesField(organization) && (<CompanyFilter />)}
          <InterestFilter />
          {!isEducational && canViewSkillsField(organization) && (<SkillFilter />)}
          {isEducational && (
            <>
              <ProgramFilter />
              <ClubFilter />
              <ClassYearFilter />
              <DegreeFilter />
            </>
          )}
        </Panel.Body>
        <Panel.Footer className="Filters__Footer">
          {isAFilterApplied && !loading && (
            <>
              <div className="Filters__Footer--header">
                <h2 className="Filters__Applied"><Trans>Applied filters</Trans></h2>
                <PlainButton className="Filters__Footer--clear" onClick={onClearQueryString}>
                  <Trans>Clear</Trans>
                </PlainButton>
              </div>
              <hr />
              <div className="Filters__Active">
                <CommunityFilterState />
                <LocationFilterState />
                <CompanyFilterState />
                <DepartmentFilterState />
                <InterestFilterState />
                <SkillFilterState />
                <ClubFilterState />
                <ClassYearFilterState />
                <ProgramFilterState />
                <DegreeFilterState />
              </div>
              <div className="Filters__Results">
                <p className="results" role="status">
                  <p>{resultsText}{" "}<Trans>that match your filters.</Trans></p>
                  <p>
                    <Trans>
                      You have selected people filters that include the following:
                    </Trans>
                  </p>
                  {options.community && (
                    <p>
                      <Trans>People</Trans>
                      <FilterGroup key="community" group={getCommunityFilter()} prefix="which" />
                    </p>
                  )}
                  <p>
                    <FilterGroup key="locations" group={filters.locations} prefix="Locations include" orRelation />
                  </p>
                  <p>
                    <FilterGroup
                      key={labels.departments.plural.toLowerCase()}
                      group={filters.departments}
                      prefix={`${labels.departments.plural} include`}
                      orRelation
                    />
                  </p>
                  <p>
                    <FilterGroup key="companies" group={filters.companies} prefix="Companies include" orRelation />
                  </p>
                  <p>
                    <FilterGroup key="interests" group={filters.interests} prefix="Interests include" orRelation />
                  </p>
                  <p>
                    {canViewSkillsField(organization) && (<FilterGroup key="skills" group={filters.skills} prefix="Skills include" orRelation />)}
                  </p>
                </p>
              </div>
            </>
          )}
        </Panel.Footer>
      </Panel>
    </>
  );
};

export default CommunityFilters;
